import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BaseHttpService, HttpResult } from '../../core/services/base-http.service';
import { VoteEntryResponseModel, VoteStatusResponseModel, VotingActivityLogRequestModel, VotingActivityLogResponseModel, VotingResponseModel } from './voting.model';
import { BaseResponse } from '../../shared/models/base-response.model';
import { Observable } from 'rxjs';
import { apiEndpoints } from '../../core/constants/api-strings';

@Injectable({
  providedIn: 'root'
})
export class VotingApiService extends BaseHttpService {

  constructor() {
    super();
  }

  // Base URL for farmer-related APIs
  private readonly farmerBaseUrl = environment.farmer_base_url;

  // Base URL for master-related APIs
  private readonly masterBaseUrl = environment.master_base_url;

  // Logged-in farmer's ID
  private farmerId = this.storage.getLoggedUserId();

  // Event code, defaults to 'KISAN25' if not set
  private eventcode = this.storage.getEventCode() || "KISAN25";

  private contestsCode = "KISAN25QUEST";


  /**
   * Retrieves the list of contest participants for the current event.
   * @returns An observable containing the HTTP result with a base response wrapping the voting response model.
   */
  getContestParticipants(): Observable<HttpResult<BaseResponse<VotingResponseModel>>> {
    let serviceName = `${this.masterBaseUrl +
      apiEndpoints.farmerDigitalCatalog.events +
      this.eventcode +
      apiEndpoints.voting.contests +
      this.contestsCode
      }`;

    return this.get<BaseResponse<VotingResponseModel>>(serviceName, {}, false);
  }

  /**
   * get vote status of the user
   */
  getVoteStatus(): Observable<HttpResult<BaseResponse<VoteStatusResponseModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId + '/' +
      apiEndpoints.farmerDigitalCatalog.events +
      this.eventcode +
      apiEndpoints.voting.contests +
      this.contestsCode +
      apiEndpoints.voting.votestatus
      }`;

    return this.get<BaseResponse<VoteStatusResponseModel>>(serviceName, {}, true);
  }

  /**
   * vote entry
   * @param participant_id Encoded participant ID to vote for.  
   * @returns Observable of HTTP result containing vote entry response.  
   */
  voteEntry(encoded_participant_id: string): Observable<HttpResult<BaseResponse<VoteEntryResponseModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId + '/' +
      apiEndpoints.farmerDigitalCatalog.events +
      this.eventcode +
      apiEndpoints.voting.contests +
      this.contestsCode +
      apiEndpoints.voting.participants + encoded_participant_id +
      apiEndpoints.voting.vote
      }`;
    return this.post<BaseResponse<VoteEntryResponseModel>>(serviceName, {}, {}, true);
  }

  /**
   * Unvotes a contest participant for the current event.  
   * @param participant_id The encoded ID of the participant to unvote.  
   * @returns Observable of HTTP result containing updated vote entry response after unvoting. 
   */
  unVoteEntry(encoded_participant_id: string): Observable<HttpResult<BaseResponse<VoteEntryResponseModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId + '/' +
      apiEndpoints.farmerDigitalCatalog.events +
      this.eventcode +
      apiEndpoints.voting.contests +
      this.contestsCode +
      apiEndpoints.voting.participants + encoded_participant_id +
      apiEndpoints.voting.vote
      }`;
    return this.delete<BaseResponse<VoteEntryResponseModel>>(serviceName, {}, true);
  }

  /**
   * @description Logs a contest activity for the current event.
   * @returns Observable of HTTP result containing voting activity log response after adding log.
   */
  addContestActivityLog(payload: VotingActivityLogRequestModel): Observable<HttpResult<BaseResponse<VotingActivityLogResponseModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId + '/' +
      apiEndpoints.farmerDigitalCatalog.events +
      this.eventcode +
      apiEndpoints.voting.contests +
      this.contestsCode +
      apiEndpoints.voting.activity
      }`;
    return this.post<BaseResponse<VotingActivityLogResponseModel>>(serviceName, payload, {}, true);
  }

}
